// components/MediaLightbox.jsx — Anamoria SPA
// v0.2 — optional per-item favorite heart (July 30, 2026). When onToggleFavorite
//   is passed, the current item shows an always-visible favorite control (top-
//   left, clear of the top-right close). Reflects items[index].isFavorite;
//   toggles via the owner's handler. Rail thumbnails favorite here (plan D-5).
// v0.1 — moment media viewer (July 30, 2026)
//   WAI-ARIA APG Dialog (Modal) + Carousel; WCAG 2.2 §2.4.11/2.4.13/2.5.7/1.1.1.
//   - Photo → full-res playbackUrl; video → <video controls>.
//   - Prev/Next are native <button>s (MANDATORY, not swipe-only — 2.5.7).
//   - Focus trapped; ESC closes; focus RETURNS to the opener. Arrow keys and
//     nav buttons change the slide but do NOT move focus (APG). Focus ring is
//     rendered above the scrim (2.4.11). Scroll locked while open.
//   Executes momentdetail-hero-precedence-lightbox-plan v0.2 (§5).
import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './MediaLightbox.module.css';

const cat = (p) => (p?.category || '').toLowerCase();
const FOCUSABLE = 'button, [href], input, video, [tabindex]:not([tabindex="-1"])';

// Shipped HeartIcon path (VoiceCard.jsx) — kept local, matching the codebase's
// per-component pattern (no shared-icon refactor this pass).
function HeartIcon({ filled }) {
  return filled ? (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export default function MediaLightbox({ items, index, title, onIndex, onClose, onToggleFavorite }) {
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const count = items.length;
  const item = items[index];

  const go = useCallback(
    (delta) => onIndex((i) => (i + delta + count) % count),
    [onIndex, count]
  );

  // Open: remember the opener, focus the close control, lock scroll.
  // Cleanup: unlock scroll and RESTORE focus to the opener (APG).
  useEffect(() => {
    openerRef.current = document.activeElement;
    const dlg = dialogRef.current;
    (dlg?.querySelector('[data-lb-initial]') || dlg)?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, []);

  // Keyboard: ESC closes, arrows navigate, Tab is trapped inside the dialog.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (count > 1 && e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
      if (count > 1 && e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
      if (e.key === 'Tab') {
        const dlg = dialogRef.current;
        const f = dlg ? Array.from(dlg.querySelectorAll(FOCUSABLE)).filter((el) => !el.disabled) : [];
        if (f.length === 0) { e.preventDefault(); return; }
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose, count]);

  if (!item) return null;

  return createPortal(
    <div className={styles.backdrop} role="presentation" onClick={onClose}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <button data-lb-initial type="button" className={styles.close} onClick={onClose} aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>

        {count > 1 && (
          <button type="button" className={styles.prev} onClick={() => go(-1)} aria-label="Previous">
            <span aria-hidden="true">‹</span>
          </button>
        )}

        <div className={styles.stage} aria-live="polite">
          <div className={styles.frame}>
            {cat(item) === 'video' ? (
              <video
                key={item.id}
                className={styles.media}
                src={item.videoNote?.playbackUrl || item.playbackUrl}
                poster={item.videoNote?.posterUrl || undefined}
                controls
                playsInline
                autoPlay
              />
            ) : (
              <img key={item.id} className={styles.media} src={item.playbackUrl} alt={title} />
            )}
            {onToggleFavorite && (
              <button
                type="button"
                className={`${styles.fav} ${item.isFavorite ? styles.favLiked : ''}`}
                onClick={() => onToggleFavorite(item.id, item.isFavorite)}
                aria-label={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                aria-pressed={!!item.isFavorite}
              >
                <HeartIcon filled={!!item.isFavorite} />
              </button>
            )}
          </div>
        </div>

        {count > 1 && (
          <button type="button" className={styles.next} onClick={() => go(1)} aria-label="Next">
            <span aria-hidden="true">›</span>
          </button>
        )}

        {count > 1 && <p className={styles.counter}>{index + 1} of {count}</p>}
      </div>
    </div>,
    document.body
  );
}
